import { useState } from 'react'
import {
  Box,
  Flex,
  Text,
  Badge,
  Table,
  IconButton,
  HStack,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  Portal,
} from '@chakra-ui/react'
import {
  FileText,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  Calendar,
  AlertTriangle,
  Play,
  Pause,
  XCircle,
} from 'lucide-react'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { useDeleteContract, useUpdateContractStatus } from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { ContractWithSupplier, ContractStatus, ContractType, BillingFrequency } from '../types'
import {
  CONTRACT_STATUS_LABELS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_TYPE_LABELS,
  BILLING_FREQUENCY_LABELS,
} from '../types'

interface ContractsListProps {
  contracts?: ContractWithSupplier[]
  searchTerm: string
  onEdit: (contract: ContractWithSupplier) => void
  onView: (contract: ContractWithSupplier) => void
  isLoading?: boolean
}

export function ContractsList({
  contracts,
  searchTerm,
  onEdit,
  onView,
  isLoading,
}: ContractsListProps) {
  const deleteContract = useDeleteContract()
  const updateStatus = useUpdateContractStatus()
  const [contractToDelete, setContractToDelete] = useState<ContractWithSupplier | null>(null)

  const filtered = contracts?.filter(
    (c) =>
      c.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.suppliers?.name && c.suppliers.name.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleDelete = async () => {
    if (!contractToDelete) return

    try {
      await deleteContract.mutateAsync(contractToDelete.id)
      toaster.create({
        title: 'Succès',
        description: 'Contrat supprimé',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la suppression',
        type: 'error',
      })
    } finally {
      setContractToDelete(null)
    }
  }

  const handleStatusChange = async (contractId: string, newStatus: ContractStatus) => {
    try {
      await updateStatus.mutateAsync({ id: contractId, status: newStatus })
      toaster.create({
        title: 'Succès',
        description: `Statut mis à jour : ${CONTRACT_STATUS_LABELS[newStatus]}`,
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la mise à jour',
        type: 'error',
      })
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR')
  }

  const formatCurrency = (value: number | null) => {
    if (!value) return '-'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const isExpiringSoon = (endDate: string | null) => {
    if (!endDate) return false
    const end = new Date(endDate)
    const today = new Date()
    const daysUntilEnd = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    return daysUntilEnd > 0 && daysUntilEnd <= 30
  }

  const isExpired = (endDate: string | null) => {
    if (!endDate) return false
    return new Date(endDate) < new Date()
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <Text color="gray.500">Chargement des contrats...</Text>
      </Box>
    )
  }

  if (!filtered || filtered.length === 0) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <FileText size={48} color="var(--chakra-colors-gray-300)" style={{ margin: '0 auto' }} />
        <Text mt={4} color="gray.500">
          {searchTerm ? 'Aucun contrat trouvé' : 'Aucun contrat enregistré'}
        </Text>
        <Text fontSize="sm" color="gray.400">
          Cliquez sur "Nouveau contrat" pour en ajouter un
        </Text>
      </Box>
    )
  }

  return (
    <>
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="gray.50">
              <Table.ColumnHeader w="120px">Référence</Table.ColumnHeader>
              <Table.ColumnHeader>Fournisseur</Table.ColumnHeader>
              <Table.ColumnHeader>Intitulé</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader>Période</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Montant TTC/an</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Statut</Table.ColumnHeader>
              <Table.ColumnHeader w="80px" textAlign="right">
                Actions
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((contract) => (
              <Table.Row key={contract.id} _hover={{ bg: 'gray.50' }} cursor="pointer">
                <Table.Cell onClick={() => onView(contract)}>
                  <Text fontFamily="mono" fontWeight="medium">
                    {contract.reference}
                  </Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(contract)}>
                  <Text fontWeight="medium">{contract.suppliers?.name || '-'}</Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(contract)}>
                  <Text>{contract.label}</Text>
                  {contract.description && (
                    <Text fontSize="xs" color="gray.500" lineClamp={1}>
                      {contract.description}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell onClick={() => onView(contract)}>
                  <HStack gap={1}>
                    <Badge colorPalette="purple" size="sm">
                      {CONTRACT_TYPE_LABELS[contract.contract_type as ContractType]}
                    </Badge>
                    {contract.billing_frequency && (
                      <Badge colorPalette="gray" size="sm">
                        {BILLING_FREQUENCY_LABELS[contract.billing_frequency as BillingFrequency]}
                      </Badge>
                    )}
                  </HStack>
                </Table.Cell>
                <Table.Cell onClick={() => onView(contract)}>
                  <Flex align="center" gap={1}>
                    <Calendar size={14} color="var(--chakra-colors-gray-400)" />
                    <Text fontSize="sm">
                      {formatDate(contract.start_date)}
                      {contract.end_date && ` - ${formatDate(contract.end_date)}`}
                    </Text>
                  </Flex>
                  {isExpiringSoon(contract.end_date) && (
                    <HStack gap={1} mt={1}>
                      <AlertTriangle size={12} color="var(--chakra-colors-orange-500)" />
                      <Text fontSize="xs" color="orange.500">
                        Expire bientôt
                      </Text>
                    </HStack>
                  )}
                  {isExpired(contract.end_date) && contract.status === 'ACTIVE' && (
                    <HStack gap={1} mt={1}>
                      <AlertTriangle size={12} color="var(--chakra-colors-red-500)" />
                      <Text fontSize="xs" color="red.500">
                        Expiré
                      </Text>
                    </HStack>
                  )}
                  {contract.is_tacit_renewal && (
                    <Text fontSize="xs" color="gray.500">
                      Tacite reconduction
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right" onClick={() => onView(contract)}>
                  <Text fontFamily="mono" fontWeight="medium">
                    {formatCurrency(contract.annual_amount_ttc)}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center" onClick={() => onView(contract)}>
                  <Badge
                    colorPalette={CONTRACT_STATUS_COLORS[contract.status as ContractStatus] || 'gray'}
                    size="sm"
                  >
                    {CONTRACT_STATUS_LABELS[contract.status as ContractStatus]}
                  </Badge>
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <MenuRoot positioning={{ placement: 'bottom-end' }}>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          <MenuItem value="view" onClick={() => onView(contract)}>
                            <ExternalLink size={16} />
                            <Text ml={2}>Voir le détail</Text>
                          </MenuItem>
                          <MenuItem value="edit" onClick={() => onEdit(contract)}>
                            <Edit size={16} />
                            <Text ml={2}>Modifier</Text>
                          </MenuItem>

                          {/* Actions de changement de statut */}
                          {contract.status === 'DRAFT' && (
                            <MenuItem
                              value="activate"
                              onClick={() => handleStatusChange(contract.id, 'ACTIVE')}
                            >
                              <Play size={16} />
                              <Text ml={2}>Activer</Text>
                            </MenuItem>
                          )}
                          {contract.status === 'ACTIVE' && (
                            <MenuItem
                              value="suspend"
                              onClick={() => handleStatusChange(contract.id, 'SUSPENDED')}
                            >
                              <Pause size={16} />
                              <Text ml={2}>Suspendre</Text>
                            </MenuItem>
                          )}
                          {contract.status === 'SUSPENDED' && (
                            <MenuItem
                              value="reactivate"
                              onClick={() => handleStatusChange(contract.id, 'ACTIVE')}
                            >
                              <Play size={16} />
                              <Text ml={2}>Réactiver</Text>
                            </MenuItem>
                          )}
                          {(contract.status === 'ACTIVE' || contract.status === 'SUSPENDED') && (
                            <MenuItem
                              value="terminate"
                              color="red.500"
                              onClick={() => handleStatusChange(contract.id, 'TERMINATED')}
                            >
                              <XCircle size={16} />
                              <Text ml={2}>Résilier</Text>
                            </MenuItem>
                          )}

                          {contract.status === 'DRAFT' && (
                            <MenuItem
                              value="delete"
                              color="red.500"
                              onClick={() => setContractToDelete(contract)}
                            >
                              <Trash2 size={16} />
                              <Text ml={2}>Supprimer</Text>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      <ConfirmDialog
        isOpen={!!contractToDelete}
        onClose={() => setContractToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le contrat"
        message={`Êtes-vous sûr de vouloir supprimer le contrat "${contractToDelete?.reference}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        colorPalette="red"
        icon={<Trash2 size={20} />}
        isLoading={deleteContract.isPending}
      />
    </>
  )
}
